import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchPoolHistory } from '../api';
import { useTheme } from '../theme';
import { fmt } from '../utils';

const PERIODS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

const CHART_HEIGHT = 160;
const PADDING = { top: 20, right: 48, bottom: 28, left: 48 };

export default function AprHistoryChart({ pool }) {
  const { colors } = useTheme();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!pool?.poolId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPoolHistory(pool.poolId)
      .then(data => {
        if (!cancelled) {
          setHistory(data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [pool?.poolId]);

  const filtered = React.useMemo(() => {
    if (!history) return [];
    const cutoff = Date.now() - period * 24 * 60 * 60 * 1000;
    return history.filter(d => new Date(d.timestamp).getTime() >= cutoff);
  }, [history, period]);

  const handleMouseMove = useCallback((e) => {
    if (!svgRef.current || filtered.length < 2) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const chartWidth = rect.width - PADDING.left - PADDING.right;
    const ratio = (x - PADDING.left) / chartWidth;
    const idx = Math.round(ratio * (filtered.length - 1));
    if (idx >= 0 && idx < filtered.length) {
      setHover(idx);
    } else {
      setHover(null);
    }
  }, [filtered]);

  const handleMouseLeave = useCallback(() => {
    setHover(null);
  }, []);

  if (loading) {
    return (
      <div style={{
        padding: '20px 24px',
        borderBottom: `1px solid ${colors.borderLight}`,
      }}>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelLight,
          letterSpacing: 1,
          marginBottom: 12,
        }}>
          APR HISTORY
        </div>
        <div style={{
          height: CHART_HEIGHT + PADDING.top + PADDING.bottom,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: colors.labelMuted,
        }}>
          Loading chart data...
        </div>
      </div>
    );
  }

  if (error || !filtered.length) {
    return (
      <div style={{
        padding: '20px 24px',
        borderBottom: `1px solid ${colors.borderLight}`,
      }}>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelLight,
          letterSpacing: 1,
          marginBottom: 12,
        }}>
          APR HISTORY
        </div>
        <div style={{
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: colors.labelMuted,
        }}>
          {error ? 'Failed to load history' : 'No data available'}
        </div>
      </div>
    );
  }

  // Compute ranges
  const apyValues = filtered.map(d => d.apy ?? 0);
  const tvlValues = filtered.map(d => d.tvlUsd ?? 0);
  const apyMin = Math.min(...apyValues);
  const apyMax = Math.max(...apyValues);
  const tvlMin = Math.min(...tvlValues);
  const tvlMax = Math.max(...tvlValues);

  const apyRange = apyMax - apyMin || 1;
  const tvlRange = tvlMax - tvlMin || 1;

  // Build SVG path data
  const viewW = 322; // fixed internal width, scales via viewBox
  const viewH = CHART_HEIGHT + PADDING.top + PADDING.bottom;
  const chartW = viewW - PADDING.left - PADDING.right;
  const chartH = CHART_HEIGHT;

  function toX(i) {
    return PADDING.left + (i / (filtered.length - 1)) * chartW;
  }
  function apyToY(val) {
    return PADDING.top + chartH - ((val - apyMin) / apyRange) * chartH;
  }
  function tvlToY(val) {
    return PADDING.top + chartH - ((val - tvlMin) / tvlRange) * chartH;
  }

  const apyPath = filtered.map((d, i) =>
    `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${apyToY(d.apy ?? 0).toFixed(1)}`
  ).join(' ');

  const tvlPath = filtered.map((d, i) =>
    `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${tvlToY(d.tvlUsd ?? 0).toFixed(1)}`
  ).join(' ');

  // Area fill under APR line
  const apyArea = apyPath
    + ` L${toX(filtered.length - 1).toFixed(1)},${(PADDING.top + chartH).toFixed(1)}`
    + ` L${PADDING.left.toFixed(1)},${(PADDING.top + chartH).toFixed(1)} Z`;

  // Y axis labels
  const apyTicks = [apyMin, (apyMin + apyMax) / 2, apyMax];
  const tvlTicks = [tvlMin, (tvlMin + tvlMax) / 2, tvlMax];

  // X axis labels
  const xTickCount = period <= 7 ? 3 : period <= 30 ? 4 : 5;
  const xTicks = [];
  for (let i = 0; i < xTickCount; i++) {
    const idx = Math.round((i / (xTickCount - 1)) * (filtered.length - 1));
    xTicks.push({ idx, date: new Date(filtered[idx].timestamp) });
  }

  const fmtDate = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  const fmtDateFull = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // Hover data
  const hoverData = hover !== null ? filtered[hover] : null;
  const hoverX = hover !== null ? toX(hover) : 0;

  return (
    <div style={{
      padding: '20px 24px',
      borderBottom: `1px solid ${colors.borderLight}`,
    }}>
      {/* Header row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14,
      }}>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelLight,
          letterSpacing: 1,
        }}>
          APR HISTORY
        </div>
        {/* Period toggles */}
        <div style={{ display: 'flex', gap: 4 }}>
          {PERIODS.map(p => (
            <button
              key={p.label}
              onClick={() => setPeriod(p.days)}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 4,
                border: `1px solid ${period === p.days ? colors.green : colors.border}`,
                background: period === p.days ? colors.greenBg : 'transparent',
                color: period === p.days ? colors.green : colors.labelMuted,
                cursor: 'pointer',
                letterSpacing: 0.5,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 12, height: 2, background: colors.green, borderRadius: 1 }} />
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelMuted,
          }}>APR</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 12, height: 2, background: colors.gold, borderRadius: 1 }} />
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelMuted,
          }}>TVL</span>
        </div>
      </div>

      {/* SVG Chart */}
      <div style={{ position: 'relative' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${viewW} ${viewH}`}
          style={{ width: '100%', height: 'auto', display: 'block' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Grid lines */}
          {apyTicks.map((_, i) => {
            const y = PADDING.top + (i / (apyTicks.length - 1)) * chartH;
            return (
              <line
                key={`grid-${i}`}
                x1={PADDING.left}
                y1={y}
                x2={viewW - PADDING.right}
                y2={y}
                stroke={colors.borderSubtle}
                strokeWidth={0.5}
              />
            );
          })}

          {/* Area fill */}
          <defs>
            <linearGradient id="aprAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.green} stopOpacity={0.12} />
              <stop offset="100%" stopColor={colors.green} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <path d={apyArea} fill="url(#aprAreaGrad)" />

          {/* TVL line */}
          <path
            d={tvlPath}
            fill="none"
            stroke={colors.gold}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.6}
          />

          {/* APR line */}
          <path
            d={apyPath}
            fill="none"
            stroke={colors.green}
            strokeWidth={1.8}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Left Y axis (APR) */}
          {apyTicks.map((val, i) => {
            const y = PADDING.top + chartH - ((val - apyMin) / apyRange) * chartH;
            return (
              <text
                key={`apy-${i}`}
                x={PADDING.left - 6}
                y={y + 3}
                textAnchor="end"
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 7,
                  fill: colors.labelMuted,
                }}
              >
                {val.toFixed(1)}%
              </text>
            );
          })}

          {/* Right Y axis (TVL) */}
          {tvlTicks.map((val, i) => {
            const y = PADDING.top + chartH - ((val - tvlMin) / tvlRange) * chartH;
            return (
              <text
                key={`tvl-${i}`}
                x={viewW - PADDING.right + 6}
                y={y + 3}
                textAnchor="start"
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 7,
                  fill: colors.labelMuted,
                }}
              >
                {fmt(val)}
              </text>
            );
          })}

          {/* X axis labels */}
          {xTicks.map(({ idx, date }, i) => (
            <text
              key={`x-${i}`}
              x={toX(idx)}
              y={viewH - 4}
              textAnchor="middle"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 7,
                fill: colors.labelMuted,
              }}
            >
              {fmtDate(date)}
            </text>
          ))}

          {/* Hover crosshair */}
          {hover !== null && (
            <>
              <line
                x1={hoverX}
                y1={PADDING.top}
                x2={hoverX}
                y2={PADDING.top + chartH}
                stroke={colors.textSecondary}
                strokeWidth={0.5}
                strokeDasharray="3,2"
              />
              <circle
                cx={hoverX}
                cy={apyToY(hoverData.apy ?? 0)}
                r={3}
                fill={colors.green}
                stroke={colors.surface}
                strokeWidth={1.5}
              />
              <circle
                cx={hoverX}
                cy={tvlToY(hoverData.tvlUsd ?? 0)}
                r={3}
                fill={colors.gold}
                stroke={colors.surface}
                strokeWidth={1.5}
              />
            </>
          )}
        </svg>

        {/* Tooltip */}
        {hover !== null && hoverData && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: hoverX > viewW / 2 ? 0 : 'auto',
            right: hoverX > viewW / 2 ? 'auto' : 0,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            padding: '8px 10px',
            pointerEvents: 'none',
            boxShadow: `0 2px 8px ${colors.shadow}`,
            zIndex: 10,
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelMuted,
              marginBottom: 4,
            }}>
              {fmtDateFull(new Date(hoverData.timestamp))}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.green,
              fontWeight: 600,
            }}>
              APR: {(hoverData.apy ?? 0).toFixed(2)}%
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.gold,
              fontWeight: 600,
              marginTop: 2,
            }}>
              TVL: {fmt(hoverData.tvlUsd ?? 0)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
